import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    LinearProgress,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateSalesFunnelReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";

const SalesFunnelReport = ({ reportType, filters, onFilterUpdate, onFilterReset, onExport, onRefresh }) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();
    const { data: productsData } = useGetProductsDropdownQuery();

    const { data: reportData, isLoading, error, refetch } = useGenerateSalesFunnelReportQuery(localFilters);

    const handleFilterChange = (field, value) => {
        // Extract values from MultipleSelectJson objects
        let processedValue = value;
        if (field.includes("_ids") && Array.isArray(value)) {
            processedValue = value.map((item) => item.value || item);
            // Remove duplicates
            processedValue = [...new Set(processedValue)];
        }

        const newFilters = { ...localFilters, [field]: processedValue };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    const handleReset = () => {
        setLocalFilters({
            start_date: null,
            end_date: null,
            region_ids: [],
            product_ids: [],
            format: "json",
        });
        onFilterReset();
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                label="Regions"
                                value={localFilters.region_ids || []}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Products
                            </InputLabel>
                            <MultipleSelectJson
                                id="product-select"
                                label="Products"
                                value={localFilters.product_ids || []}
                                onChange={(value) => handleFilterChange("product_ids", value)}
                                name="product_ids"
                                options={productsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate sales funnel report"}
                </Alert>
            )}

            {reportData && !isLoading && (
                <Box>
                    {/* Funnel Data */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Customers
                                    </Typography>
                                    <Typography variant="h4">{reportData.funnel_data?.total_customers || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Customers with Orders
                                    </Typography>
                                    <Typography variant="h4">
                                        {reportData.funnel_data?.customers_with_orders || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.funnel_data?.total_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Paid Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.funnel_data?.paid_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Warranty Registrations
                                    </Typography>
                                    <Typography variant="h4">
                                        {reportData.funnel_data?.warranty_registrations || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Insurance Renewals
                                    </Typography>
                                    <Typography variant="h4">
                                        {reportData.funnel_data?.insurance_renewals || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Conversion Rates */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Conversion Rates
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Leads to Customers: {reportData.conversion_rates?.leads_to_customers}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.conversion_rates?.leads_to_customers || 0)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                                <Box mb={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Customers to Orders: {reportData.conversion_rates?.customers_to_orders}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.conversion_rates?.customers_to_orders || 0)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                                <Box mb={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Orders to Paid: {reportData.conversion_rates?.orders_to_paid}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.conversion_rates?.orders_to_paid || 0)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box mb={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Orders to Warranty: {reportData.conversion_rates?.orders_to_warranty}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.conversion_rates?.orders_to_warranty || 0)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                                <Box mb={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Orders to Insurance: {reportData.conversion_rates?.orders_to_insurance}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={parseFloat(reportData.conversion_rates?.orders_to_insurance || 0)}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Monthly Trends */}
                    {reportData.monthly_trends && reportData.monthly_trends.length > 0 && (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Monthly Trends
                            </Typography>
                            <Grid container spacing={2}>
                                {reportData.monthly_trends.map((trend, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {trend.month}
                                                </Typography>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Orders: {trend.orders_count}
                                                </Typography>
                                                <Typography color="textSecondary">
                                                    Revenue: ₹{trend.total_amount?.toLocaleString() || 0}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}
                </Box>
            )}

            {!reportData && !isLoading && !error && (
                <Alert severity="info">No data found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default SalesFunnelReport;
